import React from 'react';
import { Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import * as ItemUtils from '../common/ItemTools';
import { EXCERPT_TEXT, LINK_BLUE, SANS_STACK, SERIF_STACK } from './common/tokens';

export const POPOVER_SURFACE = {
  background: '#fff',
  border: '2px solid #1a1a1a',
  borderRadius: '8px',
  boxShadow: '0 12px 32px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)',
  animation: 'cl-pop-in 0.15s ease-out',
  '@keyframes cl-pop-in': {
    from: { opacity: 0, transform: 'translateY(-8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

export function InitialsAvatar({
  name, size, fontSize, fontFamily,
}) {
  return (
    <Box sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: ItemUtils.authorColor(name),
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize,
      fontFamily,
      flexShrink: 0,
    }}>
      {ItemUtils.authorInitials(name)}
    </Box>
  );
}

// Shared body of every entity hovercard
export function EntityHoverCard({
  avatar, name, subheader, excerpt, openLabel, onOpen, onFilter,
}) {
  const theme = useTheme();

  return (
    <Box
      role={onOpen ? 'button' : 'group'}
      onClick={onOpen}
      sx={{
        ...POPOVER_SURFACE,
        width: 300,
        padding: '14px',
        cursor: onOpen ? 'pointer' : 'default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {avatar}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{
            fontFamily: SERIF_STACK,
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.25,
            color: '#1a1a1a',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {name}
          </Box>
          {subheader && (
            <Box sx={{
              fontFamily: SANS_STACK,
              fontSize: 12,
              color: '#666',
              textTransform: 'lowercase',
              marginTop: '2px',
            }}>
              {subheader}
            </Box>
          )}
        </Box>
      </Box>
      {excerpt && (
        <Box sx={{
          fontFamily: SERIF_STACK,
          fontSize: 13,
          lineHeight: 1.5,
          color: EXCERPT_TEXT,
          marginTop: '10px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {excerpt}
        </Box>
      )}
      {(onOpen || onFilter) && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginTop: '10px',
          paddingTop: '8px',
          borderTop: '1px solid #eee',
          fontFamily: SANS_STACK,
          fontSize: 12,
          fontWeight: 600,
        }}>
          {onOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: LINK_BLUE }}>
              {openLabel}
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Box>
          )}
          {onFilter && (
            <Box
              component="button"
              type="button"
              // The card itself navigates, so keep this action from bubbling into it.
              onClick={(e) => { e.stopPropagation(); onFilter(); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                marginLeft: 'auto',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                padding: '2px 8px',
                background: '#fff',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 600,
                color: '#3C3C3C',
                transition: 'background 0.12s, border-color 0.12s, color 0.12s',
                '&:hover': {
                  background: theme.palette.primary.tint,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                },
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 14 }} />
              Filter by
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export function useHoverCard({ hasCard, placement = 'bottom-start', renderCard }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openTimer = React.useRef(null);
  const closeTimer = React.useRef(null);

  const clearTimers = React.useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  React.useEffect(() => clearTimers, [clearTimers]);

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleClose = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    closeTimer.current = setTimeout(() => setAnchorEl(null), 200);
  };
  const handleEnter = (e) => {
    cancelClose();
    const el = e.currentTarget;
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setAnchorEl(el), 220);
  };
  const close = React.useCallback(() => {
    clearTimers();
    setAnchorEl(null);
  }, [clearTimers]);

  const triggerProps = hasCard
    ? { onMouseEnter: handleEnter, onMouseLeave: scheduleClose }
    : {};

  const popper = hasCard ? (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement={placement}
      style={{ zIndex: 1500 }}
      modifiers={[
        { name: 'offset', options: { offset: [0, 6] } },
        { name: 'preventOverflow', options: { padding: 8 } },
        { name: 'flip', enabled: true },
      ]}
    >
      <Box
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        // Keep clicks inside the (portaled) card from reaching an enclosing
        // ClickAwayListener, which would unmount the card before its own
        // click handler could navigate. Matters for co-author rows.
        onMouseDown={(e) => e.stopPropagation()}
      >
        {renderCard(close)}
      </Box>
    </Popper>
  ) : null;

  return { triggerProps, popper, close };
}
